import { useMutation, useQuery } from 'react-query'
import {
  changePassword,
  checkHeartBeat,
  forgotPassword,
  getPlatformInfo,
  login,
  logout,
  refetchAccessToken,
  registration,
  resetPassword,
} from '../../../../api/authentication'
import {
  deleteCompanyLink,
  fillTermsheet,
  getCaptableIframeToken,
  getCompanyDocuments,
  getInvestmentModellingDetails,
  getUserContext,
  postCompanyLink,
  validateInvestmentModelling,
} from '../../../../api/investorApi'
import { useAuth } from './Auth'
import { useNavigate } from 'react-router-dom'
import { graphqlRequestClient } from '../../../../queries/client'
import { setAuth } from './AuthHelpers'

const API_URL = process.env.REACT_APP_API_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/verify_token`
export const REGISTER_URL = `${API_URL}/register`

export function useLogin() {
  return useMutation({ mutationKey: 'Login', mutationFn: login })
}

export function useLogout() {
  return useMutation({ mutationKey: 'logout', mutationFn: logout })
}

export function generateRedirectUrl() {
  const redirectUrl = encodeURIComponent(window.location.href)
  const baseUrl = (
    process.env.REACT_APP_ID_SERVER_CLIENT_URL ?? 'https://identity.dev.hissa.com'
  ).replace(/\/+$/, '')
  const url = `${baseUrl}/login?redirect=${redirectUrl}&platform=investor`
  return url
}

export function useCheckHeartBeat() {
  return useQuery({
    queryFn: checkHeartBeat,
    queryKey: 'heartbeat',
    refetchInterval: 60000, // Refetch every 60 seconds (1 minute)
    refetchOnWindowFocus: 'always', // Refetch when the tab regains focus
    staleTime: 0, // Ensure data is treated as stale immediately
    cacheTime: 0, // Avoid caching if unnecessary,
    retry: false,
  })
}

export function useRefetchAccessToken(enabled: boolean) {
  return useQuery({
    queryFn: refetchAccessToken,
    queryKey: 'refetchAccessToken',
    enabled: enabled,
    onSuccess: (data: any) => {
      setAuth(data.data)
      graphqlRequestClient.setHeader('authorization', data.data)
    },
    retry: false,
  })
}

export function useForgotPassword() {
  return useMutation({ mutationKey: 'ForgotPassword', mutationFn: forgotPassword })
}

export function useResetPasswords() {
  return useMutation({ mutationKey: 'ResetPassword', mutationFn: resetPassword })
}
export function useChangePassword() {
  return useMutation({
    mutationKey: 'changePassword',
    mutationFn: changePassword,
  })
}
export function useRegistration() {
  return useMutation({
    mutationKey: 'register',
    mutationFn: registration,
  })
}

export function useGetCompanyDocuments(IframeToken: string) {
  return useQuery({
    queryFn: getCompanyDocuments,
    queryKey: ['companyDocuments', IframeToken],
  })
}

export function useUserContextQuery(auth: string, enabled: boolean) {
  return useQuery({
    queryFn: getUserContext,
    queryKey: ['userContext', auth],
    enabled,
    cacheTime: 0,
    staleTime: 0,
  })
}

export function useGetInvestmentModelling(hissaCompanyId: string) {
  return useQuery({
    queryFn: getInvestmentModellingDetails,
    queryKey: ['getInvestmentModelling', hissaCompanyId],
  })
}

export function useValidateInvestmentModelling() {
  return useMutation({
    mutationFn: validateInvestmentModelling,
    mutationKey: 'validateLink',
  })
}

export function useGetCaptableIframeToken(companyId: string) {
  return useQuery({
    queryFn: getCaptableIframeToken,
    queryKey: ['captableIframeToken', companyId],
  })
}

export function useFillTermsheet() {
  return useMutation({
    mutationFn: fillTermsheet,
    mutationKey: 'fillTermsheet',
  })
}

export function usePostCompanyDocumentLink() {
  return useMutation({
    mutationFn: postCompanyLink,
    mutationKey: 'postCompanyDocumentLink',
  })
}

export function useDeleteCompanyDocumentLink() {
  return useMutation({
    mutationFn: deleteCompanyLink,
    mutationKey: 'deleteCompanyDocumentLink',
  })
}

export function useGetPlatformInfo(accesstoken: string, enabled?: boolean) {
  return useQuery({
    queryKey: ['getPlatformInfo', accesstoken],
    queryFn: getPlatformInfo,
    enabled,
  })
}
